import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl-next';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import { jsx } from '@emotion/react';
import Button from '@atlaskit/button/new';
import Modal, { ModalBody, ModalFooter, ModalHeader, ModalTitle } from '@atlaskit/modal-dialog';
import { NUMBER_OF_REACTIONS_TO_DISPLAY } from '../../shared/constants';
import { messages } from '../../shared/i18n';
import { ReactionsList } from './ReactionsList';
import { containerStyle, titleStyle } from './styles';

/**
 * Test id for the Reactions modal dialog
 */
export var RENDER_MODAL_TESTID = 'render-reactions-modal';
var getDimensions = function getDimensions(container) {
  return {
    clientWidth: container === null || container === void 0 ? void 0 : container.clientWidth,
    scrollWidth: container === null || container === void 0 ? void 0 : container.scrollWidth,
    scrollLeft: container === null || container === void 0 ? void 0 : container.scrollLeft
  };
};
export var ReactionsDialog = function ReactionsDialog(_ref) {
  var _ref$reactions = _ref.reactions,
    reactions = _ref$reactions === void 0 ? [] : _ref$reactions,
    _ref$handleCloseReact = _ref.handleCloseReactionsDialog,
    handleCloseReactionsDialog = _ref$handleCloseReact === void 0 ? function () {} : _ref$handleCloseReact,
    emojiProvider = _ref.emojiProvider,
    selectedEmojiId = _ref.selectedEmojiId,
    _ref$handleSelectReac = _ref.handleSelectReaction,
    handleSelectReaction = _ref$handleSelectReac === void 0 ? function () {} : _ref$handleSelectReac;
  var _useState = useState(),
    _useState2 = _slicedToArray(_useState, 2),
    elementToScroll = _useState2[0],
    setElementToScroll = _useState2[1];
  var _useState3 = useState(null),
    _useState4 = _slicedToArray(_useState3, 2),
    reactionsContainerRef = _useState4[0],
    setReactionsContainerRef = _useState4[1];
  var reactionElementsRef = useRef();
  var observerRef = useRef();
  var intl = useIntl();
  var isSelectedEmojiViewed = useRef(false);
  var totalReactionsCount = useMemo(function () {
    return reactions.reduce(function (accum, current) {
      return accum += current === null || current === void 0 ? void 0 : current.count;
    }, 0);
  }, [reactions]);
  var sortedReactions = useMemo(function () {
    return reactions.sort(function (a, b) {
      return (b === null || b === void 0 ? void 0 : b.count) - (a === null || a === void 0 ? void 0 : a.count);
    });
  }, [reactions]);

  /* set Reactions Border Width , 9 Number of reactions to display*/
  var reactionsBorderWidth = useMemo(function () {
    return Math.ceil(reactions.length / NUMBER_OF_REACTIONS_TO_DISPLAY) * 100;
  }, [reactions]);

  /* Callback from IntersectionObserver to set/unset classNames based on visibility to toggle styles*/
  var handleNavigation = useCallback(function (entries) {
    entries.forEach(function (entry, index) {
      var _dataset;
      var element = entry.target;
      var emojiElement = element === null || element === void 0 ? void 0 : element.querySelector('[data-emoji-id]');
      var emojiId = emojiElement === null || emojiElement === void 0 || (_dataset = emojiElement.dataset) === null || _dataset === void 0 ? void 0 : _dataset.emojiId;
      if (entry.intersectionRatio < 1) {
        element.classList.add('disabled');
        /*Check if selectedEmoji (passed as props based on what user selects) is out of viewport */
        if (emojiId === selectedEmojiId && !isSelectedEmojiViewed.current) {
          setElementToScroll(emojiElement !== null && emojiElement !== void 0 ? emojiElement : undefined);
        }
      } else {
        if (emojiId === selectedEmojiId && !isSelectedEmojiViewed.current) {
          isSelectedEmojiViewed.current = true;
        }
        element.classList.remove('disabled');
      }
    });
  }, [selectedEmojiId]);
  useEffect(function () {
    if (elementToScroll && !isSelectedEmojiViewed.current && reactionsContainerRef) {
      isSelectedEmojiViewed.current = true;
      var parentElement = elementToScroll.closest('.reaction-elements');
      var reactionsList = document.querySelector('#reactions-dialog-tabs-list');
      var _getDimensions = getDimensions(reactionsList),
        clientWidth = _getDimensions.clientWidth;
      var offsetLeft = parentElement === null || parentElement === void 0 ? void 0 : parentElement.offsetLeft;
      /* which means emoji is not in viewport so scroll to it*/
      if (reactionsList && offsetLeft > clientWidth) {
        var scrollBy = Math.trunc(offsetLeft / clientWidth) * clientWidth;
        reactionsList.scrollLeft += scrollBy;
      }
    }
  }, [elementToScroll, reactionsContainerRef]);

  /* Set up InterSectionObserver to observer reaction elements on navigating*/
  useEffect(function () {
    if (reactionsContainerRef) {
      var options = {
        root: reactionsContainerRef,
        rootMargin: '0px',
        threshold: 1.0
      };
      observerRef.current = new IntersectionObserver(handleNavigation, options);
      reactionElementsRef.current = reactionsContainerRef.querySelectorAll('.reaction-elements');
      reactionElementsRef.current && reactionElementsRef.current.length > 0 && reactionElementsRef.current.forEach(function (child) {
        var _observerRef$current;
        observerRef === null || observerRef === void 0 || (_observerRef$current = observerRef.current) === null || _observerRef$current === void 0 || _observerRef$current.observe(child);
      });
    }
    return function () {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = undefined;
      }
    };
  }, [reactionsContainerRef, reactions, handleNavigation, selectedEmojiId]);
  var setRef = useCallback(function (node) {
    if (!reactionsContainerRef) {
      setReactionsContainerRef(node);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return jsx(Modal, {
    onClose: handleCloseReactionsDialog,
    height: 600,
    testId: RENDER_MODAL_TESTID
  }, jsx(ModalHeader, null, jsx("div", {
    css: titleStyle
  }, jsx(ModalTitle, null, intl.formatMessage(messages.reactionsCount, {
    count: totalReactionsCount
  })))), jsx(ModalBody, null, jsx("div", {
    css: containerStyle(reactionsBorderWidth),
    ref: setRef
  }, jsx(ReactionsList, {
    initialEmojiId: selectedEmojiId,
    reactions: sortedReactions,
    emojiProvider: emojiProvider,
    onReactionChanged: handleSelectReaction
  }))), jsx(ModalFooter, null, jsx(Button, {
    appearance: "primary",
    onClick: handleCloseReactionsDialog,
    autoFocus: true
  }, intl.formatMessage(messages.closeReactionsDialog))));
};